import { useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { AgentResourcesContext, AgentResourcesContextValue } from './context';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { ChatContextType } from '@trinity/chat';

type Props = {
  children: ReactNode;
};

export function AgentResourcesProvider({ children }: Props) {

  const session = useContext(ActiveChatSessionContext);

  const agentIdentifier = useMemo(() => {
    return session.context.type === ChatContextType.Agent
      ? session.context.data.agent.identifier
      : null;
  }, [session.context.type]);

  const apiClient = useChatApiClient();

  const agentResourcesQuery = useQuery({
    queryKey: ['agent-resources', agentIdentifier],
    queryFn: () => {
      return apiClient.agentResources.getAgentResources({ agentIdentifier });
    },
    enabled: !!agentIdentifier, // Only run if we have an identifier
    initialData: { dataSources: [], filters: [] }, // Provide initial empty data to avoid undefined checks
  });

  const contextValue: AgentResourcesContextValue = useMemo(() => ({
    data: agentResourcesQuery.data,
    isLoading: agentResourcesQuery.isLoading,
    isError: agentResourcesQuery.isError,
    error: agentResourcesQuery.error,
  }), [agentResourcesQuery.data, agentResourcesQuery.isLoading, agentResourcesQuery.isError, agentResourcesQuery.error]);

  return (
    <AgentResourcesContext.Provider value={contextValue}>
      {children}
    </AgentResourcesContext.Provider>
  );
}

export function useAgentResources(): AgentResourcesContextValue {
  const context = useContext(AgentResourcesContext);

  if (!context) {
    throw new Error('useAgentResources must be used within an AgentResourcesProvider');
  }

  return context;
}